import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ProductApi } from '../components/ProductApi';
import { SourceGroups } from '../components/SourceGroups';
import { Card, Empty, EntityChip, StatTile, StatusPill } from '../components/ui';
import { api } from '../lib/api';
import { useApi } from '../lib/useApi';

interface ProductRow {
  key: string;
  name: string;
  description: string | null;
  isInfrastructure: boolean;
  versions: string[];
  unversioned: number;
  drifting: boolean;
  deploymentCount: number;
  containerCount: number;
  clients: string[];
  deployments: Array<{
    id: string;
    name: string;
    version: string | null;
    versionSource: string | null;
    needsReview: boolean;
    /** Null for shared `-common` infrastructure. */
    client: { id: string; code: string; name: string } | null;
    server: { id: string; name: string; hostname: string; status: string };
    containers: number;
  }>;
  /**
   * Compose files on disk that nobody brought up. Not deployments — a
   * deployment means something was deployed — so they are listed apart and
   * excluded from every count above.
   */
  provisioned: Array<{
    id: string;
    name: string;
    client: { id: string; code: string; name: string } | null;
    server: { id: string; name: string; hostname: string; status: string };
    onDiskPath: string | null;
  }>;
}

/**
 * One card per product: what it is, which versions are deployed, and where.
 *
 * Replaces the product x client grid. The grid answered "which cell disagrees",
 * which only matters once drift exists; the question people arrive with is "what
 * is this product, where does it run, and what versions are out there".
 */
/**
 * Re-reads every deployment's OpenAPI document, now.
 *
 * This control was missing until 2026-10-15, while three different places in
 * the UI told people to "run API discovery from the Products page". The cost of
 * that was real: `knowledgeiq-adq` was upgraded to a build serving /sources and
 * PlatformIQ went on insisting the instance did not support it, because the
 * only way to refresh the spec was an API call with no button behind it.
 *
 * Separate from the per-server Refresh, which re-reads containers and metrics
 * every 60 seconds anyway. Specs are NOT on that cycle — each one is an HTTP
 * request to every exposed port of every container, which is far too much to do
 * each minute — so this is the control that picks up a product upgrade.
 */
function RefreshApis({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [canTrigger, setCanTrigger] = useState(false);

  useEffect(() => {
    api<{ canTriggerCollection: boolean }>('/capabilities')
      .then((c) => setCanTrigger(c.canTriggerCollection))
      .catch(() => setCanTrigger(false));
  }, []);

  async function run() {
    setBusy(true);
    setNote(null);
    try {
      const res = await api<{ results: Array<{ server: string; operations: number }> }>(
        '/api-discovery',
        { method: 'POST' },
      );
      const total = res.results.reduce((n, r) => n + r.operations, 0);
      setNote(`Read ${total} operations across ${res.results.length} servers.`);
      onDone();
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'API discovery failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="refresh-control">
      <button
        type="button"
        className="ghost"
        disabled={busy || !canTrigger}
        onClick={() => void run()}
        title={
          canTrigger
            ? "Re-read every container's OpenAPI document. Do this after a product is upgraded — specs are not on the 60-second cycle."
            : 'Reaches every monitored host, so it needs operator rights or above'
        }
      >
        {busy ? 'Reading specs…' : 'Refresh APIs'}
      </button>
      {note && <span className="muted small">{note}</span>}
    </span>
  );
}

export function Products() {
  const { data, error, loading, reload } = useApi<ProductRow[]>('/products');
  const [apiOpen, setApiOpen] = useState<string | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [only, setOnly] = useState<string | null>(null);

  if (loading && !data) return <p className="empty">Loading products…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;

  const all = data ?? [];
  const drifting = all.filter((p) => p.drifting);
  const real = all.filter((p) => !p.isInfrastructure);

  // Derived from what is actually deployed, never a hard-coded list. A fixed
  // list silently omits the next product — HubIQ and AssistIQ were both running
  // on this estate for weeks before anything knew their names.
  // Infrastructure included, and sorted after the client-facing products rather
  // than mixed in. It was excluded when these filters were first added, because
  // the only infrastructure then was Caddy and the registry; now MySQL, Ollama
  // and the Common stacks are real products too, and leaving them out means the
  // chips disagree with the cards below them.
  const filters = all
    .slice()
    .sort(
      (a, b) =>
        Number(a.isInfrastructure) - Number(b.isInfrastructure) || a.name.localeCompare(b.name),
    );

  // The tiles keep counting the whole estate while a filter is applied: they
  // answer "how big is this" and would be actively misleading if they shrank to
  // match a filter the reader set a moment ago.
  const products = only ? all.filter((p) => p.key === only) : all;

  return (
    <>
      <div className="page-head">
        <h1>Products</h1>
        <RefreshApis onDone={reload} />
      </div>

      {/* Quick filters. Real <button>s so they are reachable by keyboard and
          announced as pressed, rather than clickable spans. */}
      <div className="cred-tabs product-filters">
        <button type="button" className={only === null ? 'tab tab-on' : 'tab'}
          aria-pressed={only === null} onClick={() => setOnly(null)}>
          All <span className="muted">· {all.length}</span>
        </button>
        {filters.map((p) => (
          <button
            key={p.key}
            type="button"
            className={only === p.key ? 'tab tab-on' : 'tab'}
            aria-pressed={only === p.key}
            // Clicking the active filter clears it, so there is always a way
            // back without hunting for "All".
            onClick={() => setOnly(only === p.key ? null : p.key)}
            title={
              `${p.deploymentCount} deployment${p.deploymentCount === 1 ? '' : 's'}` +
              (p.isInfrastructure ? ' · shared infrastructure, not a client deliverable' : '')
            }
          >
            {/* Infrastructure is dimmed rather than hidden: it belongs in the
                list, but it is not what someone scanning for a product wants
                their eye drawn to. */}
            <span className={p.isInfrastructure ? 'muted' : undefined}>{p.name}</span>
            {p.drifting && <span className="tag tag-warn">drift</span>}
            <span className="muted"> · {p.deploymentCount}</span>
          </button>
        ))}
      </div>

      <div className="tiles">
        <StatTile label="Products" value={real.length} sub="excluding infrastructure" />
        <StatTile label="Deployments" value={products.reduce((n, p) => n + p.deploymentCount, 0)} />
        <StatTile
          label="With version drift"
          value={drifting.length}
          tone={drifting.length > 0 ? 'warn' : 'up'}
          sub={drifting.length > 0 ? drifting.map((p) => p.name).join(', ') : 'all consistent'}
        />
      </div>

      {products.map((p) => (
        <Card
          key={p.key}
          title={
            <span className="product-title">
              <EntityChip type="product" label={p.name} />
              {p.isInfrastructure && <span className="tag">infrastructure</span>}
              {/* Drift is named in words as well as ordered first, so it survives
                  greyscale, printing and colour blindness. */}
              {p.drifting && <span className="tag tag-warn">version drift</span>}
            </span>
          }
          actions={
            <span className="muted small">
              {p.deploymentCount} deployment{p.deploymentCount === 1 ? '' : 's'} · {p.containerCount} containers
            </span>
          }
        >
          {p.description && <p className="muted small">{p.description}</p>}

          <div className="version-summary">
            <span className="muted small">Versions deployed</span>
            {p.versions.map((v) => (
              <span key={v} className="ver">{v}</span>
            ))}
            {p.unversioned > 0 && (
              <span
                className="muted small"
                title="Built from an untagged or :local image, so there is no version to report"
              >
                {p.unversioned} not versioned
              </span>
            )}
            {p.versions.length === 0 && p.unversioned === 0 && <span className="muted small">—</span>}
            <button
              type="button"
              className="ghost small"
              aria-expanded={apiOpen === p.key}
              onClick={() => setApiOpen(apiOpen === p.key ? null : p.key)}
            >
              {apiOpen === p.key ? 'Hide API' : 'Show API'}
            </button>
            {/* KnowledgeIQ only. Source groups are its own concept, so the
                control appears on that product rather than as a global page
                nobody would associate with it. */}
            {p.key === 'knowledgeiq' && (
              <button
                type="button"
                className="ghost small"
                aria-expanded={sourcesOpen}
                onClick={() => setSourcesOpen(!sourcesOpen)}
              >
                {sourcesOpen ? 'Hide source groups' : 'Source groups'}
              </button>
            )}
          </div>

          {apiOpen === p.key && <ProductApi productKey={p.key} />}
          {p.key === 'knowledgeiq' && sourcesOpen && <SourceGroups />}

          {/* A defined product that is deployed nowhere. Said out loud rather
              than left off the page: this list used to be built by grouping
              deployments, so DocumentIQ, AssistIQ and KnowledgeIQ Core simply
              vanished — and "not in the list" is indistinguishable from "not a
              product we have". */}
          {p.deployments.length === 0 && p.provisioned.length === 0 && (
            <Empty>
              Defined, but deployed nowhere. Nothing is running and no compose file for it was
              found under /opt on any monitored host.
            </Empty>
          )}

          {/* Kept apart from the deployments table and out of every count above.
              A deployment means something was deployed; these are compose files
              nobody has brought up, and listing them as deployments with zero
              containers read as "two broken deployments" when the truth is none
              and two sets of files waiting. */}
          {p.provisioned.length > 0 && (
            <div className="provisioned">
              <p className="muted small">
                <strong>Not deployed.</strong> {p.provisioned.length} compose file
                {p.provisioned.length === 1 ? '' : 's'} on disk that nothing has been started from:
              </p>
              <ul>
                {p.provisioned.map((d) => (
                  <li key={d.id}>
                    <code className="stack-name">{d.name}</code>{' '}
                    {d.client ? (
                      <Link to={`/clients/${d.client.id}`}>{d.client.code}</Link>
                    ) : (
                      <span className="tag tag-common">COMMON</span>
                    )}{' '}
                    on <Link to={`/servers/${d.server.id}`}>{d.server.name}</Link>
                    {d.onDiskPath && <span className="muted small mono"> — {d.onDiskPath}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {p.deployments.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Version</th><th>Client</th><th>Server</th><th className="num">Containers</th>
                </tr>
              </thead>
              <tbody>
                {p.deployments.map((d) => (
                  <tr key={d.id}>
                    <td>
                      {d.version ? (
                        <span className="ver">{d.version}</span>
                      ) : (
                        // Never invent a version. "latest" is a tag, not a release.
                        <span className="muted" title={d.versionSource ?? 'No version-shaped image tag'}>
                          not versioned
                        </span>
                      )}
                    </td>
                    <td>
                      {d.client ? (
                        <Link to={`/clients/${d.client.id}`}>{d.client.name}</Link>
                      ) : (
                        <span className="tag tag-common" title="Shared by every client on this host">COMMON</span>
                      )}
                      {d.needsReview && (
                        <span className="tag tag-warn" title="Discovered by the collector; client inherited from the server owner">
                          needs review
                        </span>
                      )}
                    </td>
                    <td>
                      <Link to={`/servers/${d.server.id}`}>{d.server.name}</Link>{' '}
                      <StatusPill status={d.server.status} />
                    </td>
                    <td className="num">{d.containers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </Card>
      ))}

      {products.length === 0 && (
        <Empty>{only ? 'That product has no deployments.' : 'No products recorded.'}</Empty>
      )}

      <p className="muted small">
        Versions come from the image tag where the tag is version-shaped. A stack built from an
        untagged or <code>:local</code> image reports <em>not versioned</em> rather than a guess.
      </p>
    </>
  );
}
